import axios from '../axios';

import type { Track } from '../interfaces/track';
import type { Album, AlbumFullObject } from '../interfaces/albums';
import type { Pagination, PaginationQueryParams } from '../interfaces/api';
import { mapJioSaavnAlbumToSpotifyAlbum, mapJioSaavnSongToSpotifyTrack } from '../utils/jiosaavn-adapters';

const fetchNewRelases = async (_params: PaginationQueryParams = {}) => {
  return { data: { albums: { items: [], total: 0, limit: 10, offset: 0, previous: null, next: null, href: '' } as unknown as Pagination<Album> } };
};

const isDummyOrInvalidAlbum = (data: any) => {
  if (!data || !data.name || data.id === '' || data.id === '0') return true;
  if (data.songs && data.songs.length === 1 && data.songs[0]?.name?.includes('sample trailer')) return true;
  return false;
};

const fetchSongAsSingleAlbum = async (songId: string) => {
  const songRes = await axios.get<any>(`/songs/${songId}`);
  const songData = Array.isArray(songRes.data?.data) ? songRes.data.data[0] : songRes.data?.data;
  if (!songData) {
    throw new Error('Song not found');
  }
  const singleAlbum: any = {
    id: songData.id,
    name: songData.name,
    type: 'single',
    year: songData.year || (songData.releaseDate ? songData.releaseDate.split('-')[0] : '2024'),
    artists: songData.artists,
    image: songData.image,
    songCount: 1,
    songs: [songData]
  };
  return { data: mapJioSaavnAlbumToSpotifyAlbum(singleAlbum) as unknown as AlbumFullObject };
};

const fetchSongAsSingleAlbumTracks = async (songId: string, limit = 50) => {
  const songRes = await axios.get<any>(`/songs/${songId}`);
  const songData = Array.isArray(songRes.data?.data) ? songRes.data.data[0] : songRes.data?.data;
  if (!songData) {
    return {
      data: {
        href: '',
        items: [],
        limit,
        offset: 0,
        total: 0,
        previous: null,
        next: null
      } as Pagination<Track>
    };
  }
  const track = mapJioSaavnSongToSpotifyTrack(songData);
  return {
    data: {
      href: '',
      items: [track],
      limit,
      offset: 0,
      total: 1,
      previous: null,
      next: null
    } as Pagination<Track>
  };
};

const fetchAlbum = async (id: string) => {
  if (id.startsWith('track:')) {
    return fetchSongAsSingleAlbum(id.replace('track:', ''));
  }

  let resData: any = null;
  if (id && id !== '0') {
    try {
      const res = await axios.get<any>(`/albums`, { params: { id, limit: 100 } });
      resData = res.data?.data;
    } catch (e) {
      console.error(e);
    }
  }

  if (isDummyOrInvalidAlbum(resData)) {
    try {
      return await fetchSongAsSingleAlbum(id);
    } catch (e) {
      console.error(e);
    }
  }

  const mapped = mapJioSaavnAlbumToSpotifyAlbum(resData || {});
  return { data: mapped as unknown as AlbumFullObject };
};

const fetchAlbums = async (ids: string[]) => {
  const responses = await Promise.all(ids.map((id) => axios.get<any>(`/albums`, { params: { id } })));
  return { 
    data: { 
      albums: responses.map((r) => mapJioSaavnAlbumToSpotifyAlbum(r.data?.data || {})) 
    } 
  };
};

const fetchAlbumTracks = async (id: string, params: PaginationQueryParams = {}) => {
  const limit = params.limit || 50;
  const offset = params.offset || 0;
  const page = Math.floor(offset / limit) + 1;

  if (id.startsWith('track:')) {
    return fetchSongAsSingleAlbumTracks(id.replace('track:', ''), limit);
  }

  let resData: any = null;
  if (id && id !== '0') {
    try {
      const res = await axios.get<any>(`/albums`, { params: { id, limit, page } });
      resData = res.data?.data;
    } catch (e) {
      console.error(e);
    }
  }

  if (isDummyOrInvalidAlbum(resData)) {
    try {
      return await fetchSongAsSingleAlbumTracks(id, limit);
    } catch (e) {
      console.error(e);
    }
  }

  let tracks = (resData?.songs || []).map(mapJioSaavnSongToSpotifyTrack);
  const total = resData?.songCount || tracks.length;
  
  return {
    data: {
      href: '',
      items: tracks,
      limit,
      offset,
      total,
      previous: null,
      next: null
    } as Pagination<Track>
  };
};

const fetchSavedAlbums = async (params: PaginationQueryParams = {}) => {
  return { data: { items: [], total: 0, limit: 10, offset: 0, previous: null, next: null, href: '' } as unknown as Pagination<{ added_at: string; album: Album }> };
};

const saveAlbums = async (ids: string[]) => ({ data: 'OK' });

const deleteAlbums = async (ids: string[]) => ({ data: 'OK' });

export const albumsService = {
  fetchAlbum,
  fetchAlbums,
  fetchNewRelases,
  fetchSavedAlbums,
  fetchAlbumTracks,
  saveAlbums,
  deleteAlbums,
};
